// Pure utility to compute a custom weighted index for a bull row using trait SDs.
// Signature: computeCustomIndexForRow(row, weights, sdSource)
// - row: bull object with PTA fields (e.g., PtaMilk, PtaFat...)
// - weights: { traitName: numericWeight }
// - sdSource: an object (likely bullTraitMinMax[0]) containing SD entries for traits
// The function will attempt multiple SD key patterns (e.g. SdPtaMilk, PtaMilkSD) and
// will fall back to 1 when an SD is missing or zero to avoid division by zero.
export function computeCustomIndexForRow(row, weights, sdSource = {}) {
    if (!row || !weights) return 0;
    console.warn(sdSource);
    const lookupSd = (trait) => {
        if (!sdSource) return 1;
        const short = trait.replace(/^Pta/, '');
        const candidates = [
            // Common new API pattern you mentioned
            `StdDevPta${short}`,
            `StdDev${trait}`,
            `StdDev_${trait}`,
            `StdDev_Pta${short}`,
            // older/alternate patterns
            `Sd${trait}`,
            `SdPta${short}`,
            `${trait}SD`,
            `${trait}Sd`,
            `${trait}STD`,
            `${trait}STDEV`,
            `${trait}SDV`,
            `${trait}StdDev`,
            `${trait}Std`,
            // older API shapes: trait + 'SD' with Pta prefix
            `Pta${trait}SD`,
            `Pta${trait}Sd`,
        ];

        for (const key of candidates) {
            if (sdSource[key] != null && sdSource[key] !== '') {
                const v = Number(sdSource[key]);
                if (!isNaN(v) && v !== 0) return v;
            }
        }

        // try direct trait (maybe API uses trait names directly)
        if (sdSource[trait] != null) {
            const v = Number(sdSource[trait]);
            if (!isNaN(v) && v !== 0) return v;
        }
        


        return 1; // safe fallback
    };

    let total = 0;
    // Explicit ordered mapping to match the SQL-style formula you supplied.
    // Each entry maps the weight key (as used in the weights object) to the
    // field name on the row to read.
    const mapping = [
        ['PtaMilk', 'PtaMilk'],
        ['PtaFat', 'PtaFat'],
        ['PtaProtein', 'PtaProtein'],
        ['PtaFinalScore', 'PtaFinalScore'],
        ['PtaStature', 'PtaStature'],
        ['PtaStrength', 'PtaStrength'],
        ['PtaDairyForm', 'PtaDairyForm'],
        ['PtaRumpAngle', 'PtaRumpAngle'],
        ['PtaThurlWidth', 'PtaThurlWidth'],
        ['PtaRearLegs', 'PtaRearLegs'],
        ['PtaFootAngle', 'PtaFootAngle'],
        ['PtaForeUdder', 'PtaForeUdder'],
        ['PtaRearUdderHeight', 'PtaRearUdderHeight'],
        ['PtaRearUdderWidth', 'PtaRearUdderWidth'],
        ['PtaUdderCleft', 'PtaUdderCleft'],
        ['PtaUdderDepth', 'PtaUdderDepth'],
        ['PtaTeatPlacement', 'PtaTeatPlacement'],
        ['PtaTeatLength', 'PtaTeatLength'],
        ['PtaDpr', 'PtaDpr'],
        ['PtaScs', 'PtaScs'],
        ['PtaProdLife', 'PtaProdLife'],
        ['PtaCcr', 'PtaCcr'],
        ['PtaHcr', 'PtaHcr'],
        ['PtaLiv', 'PtaLiv'],
        ['PtaHti', 'PtaHti'],
        ['PtaMfv', 'PtaMfv'],
        ['PtaDab', 'PtaDab'],
        ['PtaKet', 'PtaKet'],
        ['PtaMas', 'PtaMas'],
        ['PtaMet', 'PtaMet'],
        ['PtaRpl', 'PtaRpl'],
        ['PtaEcm', 'PtaEcm'],
        ['PtaJui', 'Jui'], // weight key PtaJui applies to row field 'Jui'
        ['PtaHliv', 'PtaHliv']
    ];

    for (const [weightKey, fieldName] of mapping) {
        const w = Number(weights[weightKey]) || 0;
        if (w === 0) continue;

        // Resolve value on the row. Prefer exact fieldName, then try 'Pta' prefixed
        // and plain short name as fallbacks.
        let val = row[fieldName];
        if (val == null && String(fieldName).startsWith('Pta')) {
            // If mapping used 'PtaX' style but row may have short name or vice-versa
            const short = fieldName.replace(/^Pta/, '');
            val = row[fieldName] ?? row[short] ?? row[`Pta${short}`];
        }
        if (val == null) val = row[fieldName] ?? 0;
        let numVal = Number(val) || 0;

        // PtaScs is centered at 3 per your SQL example
        if (weightKey === 'PtaScs') {
            numVal = numVal - 3;
        }

        const sd = lookupSd(weightKey);
        total += (w * numVal) / sd;
    }

    return total;
}

export default computeCustomIndexForRow;
 